import React from 'react'

import {useFormik} from 'formik'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'
import * as Yup from 'yup'

import config from '../../../config'

import {useAppDispatch} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import {captureException} from '../../../../../commons/src/services/logging'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import {formatCurrency, formatPrice} from '../../../libs/formatter'
import {getFullRefundAmount, getPartialRefundAmount} from '../../../libs/order'
import {RefundRequest, postRefund} from '../../../services/api/partner'

import {ProviderId, ShippingId} from '../../../types'

import sendIcon from '../../../../../commons/src/assets/icons/send.svg'

import ActionBar from '../../../../../commons/src/components/action-bar'
import Button from '../../../../../commons/src/components/button'
import CheckboxField from '../../../../../commons/src/components/checkbox-field'
import DescriptionList from '../../../../../commons/src/components/description-list'
import DescriptionListItem from '../../../../../commons/src/components/description-list/DescriptionListItem'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import Icon from '../../../../../commons/src/components/icon'
import LoadingIndicator from '../../../../../commons/src/components/loading-indicator'
import Modal from '../../../../../commons/src/components/modal'
import NumberInputField from '../../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../../commons/src/components/paragraph'
import RadioGroupField from '../../../../../commons/src/components/radio-group-field'
import RadioGroupItem from '../../../../../commons/src/components/radio-group-field/radio-group-item'
import SelectField from '../../../../../commons/src/components/select-field'
import SelectMenu from '../../../../../commons/src/components/select-menu'
import SelectMenuItem from '../../../../../commons/src/components/select-menu/SelectMenuItem'
import Stack from '../../../../../commons/src/components/stack'
import TextField from '../../../../../commons/src/components/text-field'
import OrderTag from '../../../components/order-tag'

import {
  AdditionalCostId,
  Order,
  RefundCustomAmountType,
  RefundQuote,
  RefundType,
} from '../../../order-types'
import AdditionalCostPartial from '../../order/additional-cost-partial'
import ModelTablePartial from '../../order/model-table-partial'

type CancellationReasonOptions = {
  value: string
  label: string
  headline?: boolean
}

export type RefundRequestFormData = {
  refundType: RefundType | undefined
  reason: string
  quotes: RefundQuote[]
  shippings: ShippingId[]
  additionalCosts: AdditionalCostId[]
  customAmount: {
    of: RefundCustomAmountType
    net: string
    description: string
  }
  formikGeneral: string
}

const customAmountOfSchema = Yup.mixed<RefundCustomAmountType>().oneOf(
  Object.values(RefundCustomAmountType)
)
const customAmountNetSchema = Yup.number().moreThan(0, 'Please enter a refund amount.')
const customAmountDescriptionSchema = Yup.string()
  .min(
    config.refund.descriptionMinLength,
    `Your description length shoud have a minimum of ${config.refund.descriptionMinLength} chars.`
  )
  .max(
    config.refund.descriptionMaxLength,
    `Your description length exceeds the maximum of ${config.refund.descriptionMaxLength} chars.`
  )

const validationSchema = {
  reason: Yup.string().required('Please select a cancellation reason.'),
  refundType: Yup.mixed<RefundType>()
    .oneOf(Object.values(RefundType))
    .required()
    .default(undefined),
  additionalCosts: Yup.array().required(),
  quotes: Yup.array<RefundQuote>().required(),
  shippings: Yup.array().required(),
  customAmount: Yup.object().when(['refundType'], {
    is: (refundType: RefundType) => refundType === RefundType.CUSTOM,
    then: schema =>
      schema.shape({
        of: customAmountOfSchema.required(),
        net: customAmountNetSchema.required('Please enter a refund amount.'),
        description: customAmountDescriptionSchema.required('Please enter a description.'),
      }),
    otherwise: schema =>
      schema.shape({
        of: customAmountOfSchema,
        net: customAmountNetSchema,
        description: customAmountDescriptionSchema,
      }),
  }),
}

type Props = {
  order: Order
  providerId: ProviderId
  onConfirm: () => void
}

const RequestRefundModal = ({order, providerId, onConfirm}: Props) => {
  const dispatch = useAppDispatch()

  const currency =
    order.amounts.cartItems?.currency ||
    order.amounts.shipping?.currency ||
    order.amounts.additionalCosts?.currency ||
    ''

  const formik = useFormik<RefundRequestFormData>({
    initialValues: {
      reason: '',
      // We don't wanna have a default here to prevent partners just clicking through without considering other refund types
      refundType: undefined,
      quotes: [] as RefundQuote[],
      additionalCosts: [] as string[],
      shippings: [] as string[],
      formikGeneral: '',
      customAmount: {
        of: RefundCustomAmountType.PRODUCTION,
        net: '',
        description: '',
      },
    },
    validationSchema: Yup.object().shape(validationSchema),
    onSubmit: values => {
      let payload: RefundRequest = {refundType: RefundType.FULL, reason: values.reason}

      if (values.refundType === RefundType.PARTIAL) {
        payload = {
          refundType: RefundType.PARTIAL,
          reason: values.reason,
          quotes: values.quotes,
          additionalCosts: values.additionalCosts,
          shippings: values.shippings,
        }
      }
      if (values.refundType === RefundType.CUSTOM) {
        payload = {
          refundType: RefundType.CUSTOM,
          reason: values.reason,
          customAmount: {
            of: values.customAmount.of,
            net: parseFloat(values.customAmount?.net),
            description: values.customAmount.description,
          },
        }
      }

      return postRefund(order.orderId, payload)
        .then(() => {
          dispatch(closeModal())
          // update order
          onConfirm()
        })
        .catch(error => {
          captureException(error, 'default', {
            providerId,
            payload,
          })
          formik.setFieldError(
            'formikGeneral',
            `Submission failed with: ${JSON.parse(error.body).error}`
          )
        })
    },
  })

  const cancellationReasonOptions = React.useMemo(() => {
    const reasonOptions: CancellationReasonOptions[] = []
    config.cancellationReasonCategories.forEach(category => {
      if (category.visibleFor.includes(order.printingService.status)) {
        const categoryReasons =
          config.cancellationReasonOptionsMap[
            category.key as keyof typeof config.cancellationReasonOptionsMap
          ]
        reasonOptions.push({
          value: category.key,
          label: category.label,
          headline: true,
        })
        categoryReasons.forEach(reason => {
          reasonOptions.push(reason)
        })
      }
    })

    return reasonOptions
  }, [order])

  const getTotalRefundAmount = () => {
    switch (formik.values.refundType) {
      case RefundType.FULL:
        return getFullRefundAmount(order)
      case RefundType.PARTIAL:
        return getPartialRefundAmount(
          order,
          formik.values.quotes,
          formik.values.additionalCosts,
          formik.values.shippings
        )
      case RefundType.CUSTOM:
        return parseFloat(formik.values.customAmount?.net) || 0
      default:
        return 0
    }
  }

  const totalRefundAmount = getTotalRefundAmount()

  const toggleFieldValue = (
    isActive: boolean,
    field: 'quotes' | 'additionalCosts' | 'shippings',
    value: string
  ) => {
    if (isActive) {
      formik.setFieldValue(field, [...formik.values[field], value])
    } else {
      formik.setFieldValue(
        field,
        formik.values[field].filter(id => id !== value)
      )
    }
  }

  const renderFullRefundDetails = () => (
    <DescriptionList noRowGap>
      {order.amounts.cartItems && (
        <DescriptionListItem label="Production">
          <strong>{formatPrice(order.amounts.cartItems.netPrice, currency, 2)}</strong>
        </DescriptionListItem>
      )}
      {order.amounts.shipping && (
        <DescriptionListItem label="Shipping">
          <strong>{formatPrice(order.amounts.shipping.netPrice, currency, 2)}</strong>
        </DescriptionListItem>
      )}
      {order.amounts.additionalCosts && (
        <DescriptionListItem label="Additional Costs">
          <strong>{formatPrice(order.amounts.additionalCosts.netPrice, currency, 2)}</strong>
        </DescriptionListItem>
      )}
    </DescriptionList>
  )

  const renderPartialRefundDetails = () => (
    <Stack space="300" className="u-margin-left-l">
      {order.quotes.length > 0 && (
        <Stack>
          <Headline size="s" minor label="Models" />
          <ModelTablePartial formik={formik} order={order} quotes={order.quotes} minified />
        </Stack>
      )}
      {order.amounts.additionalCosts && (
        <Stack>
          <Headline size="s" minor label="Additional Costs" />
          {orderBy(order.additionalCosts, item => item.created, 'desc')
            .filter(ac => ac.status.paid)
            .map(additionalCost => (
              <ActionBar key={additionalCost.created} noWrap alignItems="top">
                <CheckboxField
                  checked={formik.values.additionalCosts.includes(additionalCost.id)}
                  onChange={value => toggleFieldValue(value, 'additionalCosts', additionalCost.id)}
                  disabled={formik.isSubmitting}
                />
                <AdditionalCostPartial additionalCost={additionalCost} hidePostFix />
              </ActionBar>
            ))}
        </Stack>
      )}
      {!!order.shipping && (
        <Stack>
          <Headline size="s" minor label="Shipping" />
          <ActionBar noWrap alignItems="top">
            <CheckboxField
              checked={formik.values.shippings.includes(order.shipping.shippingId)}
              onChange={value => toggleFieldValue(value, 'shippings', order.shipping.shippingId)}
              disabled={formik.isSubmitting}
            />
            <Paragraph className="u-no-margin-bottom" size="s">
              {order.shipping.name}
            </Paragraph>
            {order.shipping.type === 'express' && <OrderTag type="express" />}
            <Paragraph className="u-no-margin-bottom" strong size="s">
              {formatPrice(order.amounts.shipping.netPrice, currency, 2)}
            </Paragraph>
          </ActionBar>
        </Stack>
      )}
    </Stack>
  )

  const renderCustomRefundDetails = () => (
    <Stack space="300" className="u-margin-left-l">
      <Stack>
        <FormRow layout="half-half">
          <FieldNote
            appearance="error"
            note={formik.submitCount > 0 && formik.errors.customAmount?.of}
          >
            <Headline size="s" minor label="Refund amount of" />
            <RadioGroupField
              className="u-no-margin-bottom"
              horizontal
              name="pricingType"
              value={formik.values.customAmount.of}
              onChange={value => formik.setFieldValue('customAmount.of', value)}
              disabled={formik.isSubmitting}
            >
              <RadioGroupItem label="Production" value={RefundCustomAmountType.PRODUCTION} />
              <RadioGroupItem label="Shipping" value={RefundCustomAmountType.SHIPPING} />
            </RadioGroupField>
          </FieldNote>
        </FormRow>
        <FormRow layout="s-l">
          <Stack space="70">
            <FieldNote
              appearance="error"
              note={formik.submitCount > 0 && formik.errors.customAmount?.net}
            >
              <NumberInputField
                allowDecimals
                allowOnly2Decimals
                placeholder="Refund amount"
                name="customAmount.net"
                value={formik.values.customAmount.net}
                suffix={formatCurrency(order.customer.currency)}
                appearance={
                  formik.submitCount > 0 && !!formik.errors.customAmount?.net ? 'error' : undefined
                }
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting}
              />
            </FieldNote>
            <Paragraph
              minor
              size="xs"
              error={
                formik.values.customAmount.description.length > config.refund.descriptionMaxLength
              }
              className="u-no-margin-bottom"
            >
              <strong>Note:</strong> the refund amount is in the customer&apos;s currency.
            </Paragraph>
          </Stack>
        </FormRow>
      </Stack>
      <FormRow>
        <FieldNote
          appearance="error"
          note={formik.submitCount > 0 && formik.errors.customAmount?.description}
        >
          <Headline size="s" minor label="Refund description" />
          <Paragraph size="xs" minor>
            <strong>Note:</strong> The information added in the description section will appear in
            the refund confirmation. So keep it short and simple to identify the reason for the
            refund.
          </Paragraph>
          <TextField
            placeholder="What is the refund about?"
            name="customAmount.description"
            rows={2}
            maxLength={config.refund.descriptionMaxLength}
            appearance={
              formik.submitCount > 0 && !!formik.errors.customAmount?.description
                ? 'error'
                : undefined
            }
            onChange={(v, n, event) => formik.handleChange(event)}
            value={formik.values.customAmount.description}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting}
          />
        </FieldNote>
      </FormRow>
    </Stack>
  )

  return (
    <Modal
      size="xl"
      headline={<Headline label={`Request to refund order #${order.orderNumber}`} size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button
            hierarchy="ghost"
            onClick={() => dispatch(closeModal())}
            disabled={formik.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={preventDoubleClick(() => formik.handleSubmit())}
            icon={formik.isSubmitting ? <LoadingIndicator /> : <Icon source={sendIcon} />}
            disabled={
              formik.isSubmitting ||
              (formik.submitCount > 0 && !isEmpty(formik.errors)) ||
              totalRefundAmount === 0
            }
          >
            Submit
          </Button>
        </>
      }
      footerContent={
        <Stack space="60">
          <Stack space="60">
            <ActionBar noWrap>
              <Paragraph size="l" className="u-no-margin-bottom">
                Total amount to refund:
              </Paragraph>
              <Paragraph strong size="l" className="u-no-margin-bottom">
                {formatPrice(
                  totalRefundAmount,
                  formik.values.refundType === RefundType.CUSTOM
                    ? order.customer.currency
                    : currency,
                  2
                )}
              </Paragraph>
            </ActionBar>
            {formik.values.refundType === RefundType.PARTIAL && (
              <Paragraph size="xs" minor className="u-no-margin-bottom">
                <strong>Excl. minimum production cost. </strong>Any minimum production cost
                difference will be automatically refunded if all production items are refunded.
              </Paragraph>
            )}
          </Stack>
          {formik.errors.formikGeneral && (
            <Paragraph className="u-no-margin-bottom" error>
              {formik.errors.formikGeneral}
            </Paragraph>
          )}
        </Stack>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack space="500">
          <Paragraph className="u-no-margin-bottom">
            Please fill out the cancellation form below. Depending on the refund amount, the order
            will either be refunded directly or our support team will be in touch to understand
            further details.
          </Paragraph>
          <FormRow layout="half-half">
            <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.reason}>
              <Headline size="s" label="Please select cancellation reason:" />
              <SelectField
                name="reason"
                placeholder="reason"
                value={cancellationReasonOptions.find(({value}) => value === formik.values.reason)}
                menu={
                  <SelectMenu values={cancellationReasonOptions}>
                    {({value, label, headline}, selected) => (
                      <SelectMenuItem
                        variant={headline ? 'headline' : undefined}
                        selected={selected}
                      >
                        {label || `${value}`}
                      </SelectMenuItem>
                    )}
                  </SelectMenu>
                }
                onChange={option => {
                  formik.setFieldValue('reason', option.value)
                }}
                onBlur={formik.handleBlur}
                appearance={formik.submitCount > 0 && !!formik.errors.reason ? 'error' : undefined}
                disabled={formik.isSubmitting}
              />
            </FieldNote>
          </FormRow>
          <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.refundType}>
            <Headline size="s" label="Please select the type of refund:" />
            <RadioGroupField
              horizontal
              name="refundType"
              value={formik.values.refundType}
              onChange={value => formik.setFieldValue('refundType', value)}
              disabled={formik.isSubmitting}
            >
              <RadioGroupItem label="Full" value={RefundType.FULL} />
              <RadioGroupItem label="Partial" value={RefundType.PARTIAL} />
              <RadioGroupItem label="Custom" value={RefundType.CUSTOM} />
            </RadioGroupField>
          </FieldNote>
          {!!formik.values.refundType && (
            <Stack>
              <Headline
                size="s"
                label={
                  formik.values.refundType === RefundType.PARTIAL
                    ? 'Refund items:'
                    : 'Refund details:'
                }
              />
              {formik.values.refundType === RefundType.FULL && renderFullRefundDetails()}
              {formik.values.refundType === RefundType.PARTIAL && renderPartialRefundDetails()}
              {formik.values.refundType === RefundType.CUSTOM && renderCustomRefundDetails()}
            </Stack>
          )}
        </Stack>
      </form>
    </Modal>
  )
}

export default RequestRefundModal
